/**
 * LocationPermissionPrompt — first-launch banner that asks for location +
 * motion-and-orientation access.
 *
 * Behaviour:
 *  - Runs once per browser session (sessionStorage flag).
 *  - For Android Chrome + desktop: a single tap to "Enable" calls
 *    `useUserPuck.request()` which kicks off the browser's native
 *    geolocation prompt and (on Android) the DeviceOrientation listener.
 *  - For iOS Safari: the same tap is required because
 *    DeviceOrientationEvent.requestPermission() MUST be inside a user
 *    gesture — we can't skip the banner there.
 *  - Dismiss button stores the dismissal so we don't nag this session.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Compass, X } from "lucide-react";

import useUserPuck from "@/shared/maps/useUserPuck";
import { surfaceStyle, surfaceClass } from "@/shared/motion/surface";

// Persist permission state across sessions — once a rider grants
// location, we don't want to ask again on every app launch.
const STORAGE_KEY = "link.locperm.asked";
const LEGACY_SESSION_KEY = "link.locperm.asked"; // older builds wrote to sessionStorage

function readStored() {
  // Check localStorage first; fall back to legacy sessionStorage so an
  // in-progress session doesn't suddenly re-prompt after this rollout.
  try { const v = window.localStorage?.getItem(STORAGE_KEY); if (v) return v; } catch { /* ignore */ }
  try { return window.sessionStorage?.getItem(LEGACY_SESSION_KEY) || null; } catch { return null; }
}
function writeStored(value) {
  try { window.localStorage?.setItem(STORAGE_KEY, value); } catch { /* ignore */ }
  // Mirror to sessionStorage so the old guards still see it.
  try { window.sessionStorage?.setItem(LEGACY_SESSION_KEY, value); } catch { /* ignore */ }
}

export default function LocationPermissionPrompt() {
  const [open, setOpen] = useState(false);
  const puck = useUserPuck({ active: false }); // request() is gestureful

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (readStored()) return;
      // If the browser already has geolocation permission granted, don't
      // re-prompt. We still wait for a tap on iOS (motion permission
      // needs a user gesture), so only auto-skip when the user has
      // already granted location AND we're not on an iOS device that
      // requires the motion-gesture banner.
      try {
        if (navigator.permissions?.query) {
          const status = await navigator.permissions.query({ name: "geolocation" });
          if (!cancelled && status.state === "granted") {
            writeStored("granted");
            return;
          }
        }
      } catch { /* permissions API unavailable — fall through to banner */ }
      if (cancelled) return;
      // Wait for the user to start interacting with the app so we don't
      // mount the banner mid-route-transition.
      const t = setTimeout(() => { if (!cancelled) setOpen(true); }, 1200);
      return () => clearTimeout(t);
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = (asked) => {
    writeStored(asked ? "1" : "dismissed");
    setOpen(false);
  };

  const enable = async () => {
    await puck.request();
    dismiss(true);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-x-0 z-[90] px-3 pointer-events-none"
          style={{
            // Sit above the booking sheet so the prompt never
            // overlaps the dark sheet — overlap was making the
            // translucent surface bleed dark and washing out the
            // body copy. Sheet height + bottom margin + safe area
            // adds up to ~167px on the rider home, +12px buffer.
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 180px)",
          }}
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 280 }}
          data-testid="location-permission-prompt"
        >
          <div
            className={`pointer-events-auto max-w-md mx-auto rounded-2xl bg-white p-4 ${surfaceClass()}`}
            style={surfaceStyle("card")}
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-10 h-10 rounded-full bg-[#1664E0]/10 text-[#1664E0] inline-flex items-center justify-center">
                <Compass size={20} strokeWidth={1.9} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-semibold text-[#0A1128]">Enable location and motion</div>
                <p className="text-[12px] text-[#384459] mt-0.5 leading-snug">
                  Link uses your location to show where you are on the map and your phone&rsquo;s compass to keep the map pointed the right way.
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={enable}
                    className="h-9 px-4 rounded-full bg-[#ed6500] hover:bg-[#ff7b1f] text-white text-[11px] tracking-[0.18em] uppercase font-semibold link-pressable"
                    data-testid="location-permission-enable"
                  >
                    Enable
                  </button>
                  <button
                    type="button"
                    onClick={() => dismiss(false)}
                    className="h-9 px-3 rounded-full text-[#384459] hover:text-[#0A1128] text-[11px] tracking-[0.18em] uppercase font-semibold transition-colors"
                    data-testid="location-permission-dismiss"
                  >
                    Not now
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={() => dismiss(false)}
                className="shrink-0 w-7 h-7 rounded-full text-[#384459] hover:text-[#0A1128] inline-flex items-center justify-center"
                aria-label="Close"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
